/**
 * The e-signature half of a save: who signs, and which supporting documents
 * were uploaded.
 *
 * This is the only source the back end has for either fact. Signer visibility
 * depends on gating conditions and repeatable-section instance values, neither
 * of which survives `buildApplicationSnapshot` (it sends bare field ids); and
 * uploaded-file metadata is never persisted server-side at all — it lives in
 * FormShell's `UploadedFilesStore` and nowhere else.
 *
 * What is deliberately NOT decided here: signer ids, ordering, application
 * roles, bindings, text anchors, delivery file names, and the `MANDATORY` /
 * `OPTIONAL` category. All of those are composed by `BmoDataBuilder`
 * server-side from the workflow template, so the browser cannot shift them.
 * Documents are identified by S3 key rather than filename for the same reason —
 * the key is what proves the file is this advisor's.
 */
import {
  type AdvisorIdentity,
  buildFieldSections,
  buildSignerRoster,
  collectGeneratedPdfEntryIds,
  resolveSignerContact,
  rolesWithNothingToSign,
  suppressedSignatures,
} from '@bmo/form-core';

import type { ESignSubmission } from '@/api/applications';
import type { SignatoryRole, WorkflowTemplate } from '@/api/workflows';

import type { FieldValue } from './FieldRenderer';
import type { UploadedEntryMeta, UploadedFilesStore } from './uploadedFiles';

/**
 * Builds the block. Always returns one — `{ signers: [], docs: [] }` while the
 * advisor has resolved no roster and uploaded nothing.
 *
 * Empty rather than undefined so the save request has a consistent shape on
 * every call. The BFF omits `saveRequest.bmoData` entirely while the roster is
 * empty; once there IS a roster it sends the object from that save onward,
 * partially populated, so `applicantInfo` reaches BMO as the advisor fills the
 * form rather than only at the e-signature step (see `BmoDataBuilder.Build`).
 * That is why the roster travels on every save and not just the last one.
 */
export function buildESignSubmission(args: {
  template: WorkflowTemplate;
  values: Record<string, FieldValue>;
  uploadedFiles: UploadedFilesStore;
  /** `StepFileEntry.id` → entry metadata, for the named-entry description. */
  entryMeta: Record<string, UploadedEntryMeta>;
  advisor: AdvisorIdentity;
  /**
   * The language this application generates its PDFs in. Decides which
   * placements count when working out that a signer has nothing left to sign —
   * EN and FR are independent placements and only one language ships.
   */
  language: 'en' | 'fr';
}): ESignSubmission {
  const { template, values, uploadedFiles, entryMeta, advisor, language } = args;

  const roles = eSignRoles(template);
  const sectionByField = buildFieldSections(template);
  // A signer whose every signature was conditioned out comes off the roster
  // entirely, exactly as a signer whose own role conditions failed does. The
  // same call feeds the e-signature step's signer list, so what the advisor
  // reviews and what reaches BMO are one resolution rather than two lookalikes.
  const nothingToSign = rolesWithNothingToSign(template, values, language);
  // NOT filtered on `roleExpectsSignature`, unlike the e-signature step's table.
  // The roster feeds BOTH halves of `bmoData`, and a role marked as expecting no
  // signatures is still an applicant on the application — dropping it here would
  // take its identity out of `applicantInfo`, which is the one thing it exists to
  // populate. `BmoDataBuilder` owns the split: it reads the whole roster for
  // `applicantInfo` and only the signing part for `eSignInfo.signerInfo`.
  const signers = buildSignerRoster(roles, sectionByField, values, nothingToSign).map(
    ({ role, instanceIdx }) => {
      const contact = resolveSignerContact(role, values, sectionByField, instanceIdx, advisor);
      return {
        roleSlug: role.slug,
        instanceIndex: instanceIdx,
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email ?? '',
        phone: contact.phone ?? '',
        // Raw, exactly as the mapped field answered. The BFF normalizes it and
        // owns the fallback when it is absent or unrecognized.
        language: contact.language ?? undefined,
      };
    },
  );

  return {
    signers,
    docs: collectSupportingDocs(uploadedFiles, entryMeta),
    generatedPdfEntryIds: collectGeneratedPdfEntryIds(template, values),
    suppressedSignatures: suppressedSignatures(template, values),
  };
}

/**
 * The PDF entries this application actually produces a document for.
 *
 * Lives in `@bmo/form-core` (`signatureConditions.ts`) because
 * `rolesWithNothingToSign` needs it too. Re-exported from here because this is
 * where it has always been imported from — by `AdditionalDocumentsStep`, which
 * needs the same count (not the ids) to offset additional-document
 * `sequenceInPackage` numbers so they continue after the generated PDFs; spec
 * §14.1's `SEQUENCE_IN_PACKAGE` is one running order across the whole package,
 * PDFs first.
 */
export { collectGeneratedPdfEntryIds };

/** The signer roles authored on this workflow's e-signature step, if it has one. */
function eSignRoles(template: WorkflowTemplate): SignatoryRole[] | undefined {
  for (const step of template.steps) {
    if (step.type === 'OneSpanESign' && step.oneSpanConfig) return step.oneSpanConfig.roles;
  }
  return undefined;
}

/**
 * Every uploaded file that is actually deliverable.
 *
 * Two exclusions, and they mean different things. No `s3Key` is a file that
 * never reached S3 — a preview pick, an upload still in flight, or one that
 * failed. A non-`clean` scan state is a file that reached S3 but has not been
 * cleared by GuardDuty (ADR-0024); naming it to BMO would promise a document
 * the delivery pipeline is not allowed to send.
 */
function collectSupportingDocs(
  store: UploadedFilesStore,
  entryMeta: Record<string, UploadedEntryMeta>,
): ESignSubmission['docs'] {
  const docs: ESignSubmission['docs'] = [];

  for (const [entryId, files] of Object.entries(store.entries)) {
    // A named entry has no advisor-typed description — the originator's entry
    // label is what identifies the document ("Void cheque"), and the server reads
    // that same label off the entry as the DOCNAME.
    const description = entryMeta[entryId]?.label ?? '';
    for (const f of files) {
      if (!f.s3Key || f.scanState !== 'ready') continue;
      // `entryId` and the sequence travel so the SAVE resolves the identical §14.1 name the
      // deliver call is about to send. They are the same inputs to the same server-side
      // resolver; sending one and not the other is how the two would drift.
      docs.push({
        s3Key: f.s3Key,
        description,
        entryId,
        sequenceInPackage: f.sequenceInPackage,
      });
    }
  }

  for (const f of store.bucket) {
    if (!f.s3Key || f.scanState !== 'ready') continue;
    // Free-form uploads carry the advisor's own description; falling back to the
    // filename keeps BMO's required field populated when they typed nothing. No
    // entryId — the server derives the DOCNAME from the description instead.
    docs.push({
      s3Key: f.s3Key,
      description: f.description?.trim() || f.file.name,
      sequenceInPackage: f.sequenceInPackage,
    });
  }

  return docs;
}
